import {
    Box,
    Typography,
    Button,
    IconButton,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import { BANNER_AND_SLIDER_HEIGHT_MOBILE_800 } from '../../utils/banner-and-slider-heights'

export const StyledPageWrapper = styled(Box)({})

export const StyledSliderContainer = styled(Box)(({ theme }) => ({
    position: 'relative',
    height: 550,
    overflow: 'hidden',

    [theme.breakpoints.down('md')]: {
        height: BANNER_AND_SLIDER_HEIGHT_MOBILE_800,
    },
}))

interface StyledSlideProps {
    isActive: boolean
}

export const StyledSlide = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'isActive',
})<StyledSlideProps>(({ isActive }) => ({
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    opacity: isActive ? 1 : 0,
    transition: 'opacity 1.5s ease-in-out',
    pointerEvents: isActive ? 'auto' : 'none',
}))

export const StyledBannerContent = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'flex-end',
    gap: theme.spacing(6),
    padding: theme.spacing(6),
    width: '100%',

    [theme.breakpoints.down('md')]: {
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
    },
}))

export const StyledPoster = styled(Box)(({ theme }) => ({
    width: 220,
    height: 330,
    borderRadius: Number(theme.shape.borderRadius) * 2,
    overflow: 'hidden',
    flexShrink: 0,
    boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
    border: `1px solid ${theme.palette.divider}`,

    [theme.breakpoints.down('md')]: {
        width: 180,
        height: 270,
        marginBottom: theme.spacing(2),
    },
}));


export const StyledPosterFallback = styled(Box)(({ theme }) => ({
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.background.default,
    color: theme.palette.text.disabled,
    fontWeight: 600,
}))

export const StyledBannerInfo = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    maxWidth: 600,

    [theme.breakpoints.down('md')]: {
        alignItems: 'center',
        textAlign: 'center',
    },
}));


export const StyledFeaturedLabel = styled(Typography)(({ theme }) => ({
    color: theme.palette.brand.main,
    fontSize: 12,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 2,
}))

export const StyledTitle = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.primary,
    fontWeight: 700,
    fontSize: 48,
    lineHeight: 1.1,
    [theme.breakpoints.down('md')]: {
        fontSize: 24,
    },
}))

export const StyledMetaWrapper = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
}))

interface StyledRatingProps {
    value: number;
}

export const StyledRating = styled(Typography, {
    shouldForwardProp: (prop) => prop !== "value",
})<StyledRatingProps>(({ theme, value }) => {
    let color = theme.palette.green.main;

    if (value < 4) {
        color = theme.palette.error.main;
    } else if (value < 7) {
        color = theme.palette.warning.main;
    }

    return {
        color,
        fontSize: 16,
        fontWeight: 600,
        display: "flex",
        alignItems: "center",
        gap: 4,
    };
});

export const StyledMetaText = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontSize: 16,
}))

export const StyledGenreWrapper = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
}))

export const StyledGenreChip = styled(Box)(({ theme }) => ({
    border: `1px solid ${theme.palette.brand.main}`,
    //   border: `1px solid ${alpha(theme.palette.brand.main, 0.35)}`,
    color: theme.palette.text.secondary,
    //   color: theme.palette.brand.main,
    padding: "6px 14px",
    borderRadius: 20,
    fontSize: 13,
}));

export const StyledOverview = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontSize: 15,
    lineHeight: 1.6,
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
}))

export const StyledButtonWrapper = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center',
    marginTop: theme.spacing(1),
}))

export const StyledPrimaryButton = styled(Button)(({ theme }) => ({
    color: theme.palette.text.primary,
    padding: '12px 28px',
    fontSize: 15,
    fontWeight: 500,
    border: `1px solid ${theme.palette.divider}`,
    '&:hover': {
        borderColor: theme.palette.primary.main,
    },
}))

// Rendered as a real router link (component={RouterLink}) so right-click works
export const StyledArrowButton = styled(IconButton)<{ component?: React.ElementType; to?: string }>(({ theme }) => ({
    border: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.primary,
    padding: 12,
    '&:hover': {
        borderColor: theme.palette.primary.main,
    },
}))

export const StyledDotsWrapper = styled(Box)(({ theme }) => ({
    position: 'absolute',
    bottom: theme.spacing(3),
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    gap: theme.spacing(1),
    zIndex: 10,
}))

interface StyledDotProps {
    active: boolean
}

export const StyledDot = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'active',
})<StyledDotProps>(({ theme, active }) => ({
    width: active ? 24 : 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: active ? theme.palette.brand.main : theme.palette.divider,
    // backgroundColor: active ? theme.palette.primary.main : theme.palette.divider,
    transition: 'all 0.3s ease',
    cursor: 'pointer',
}))

export const StyledSectionWrapper = styled(Box)(({ theme }) => ({
    marginTop: theme.spacing(6),
}))

export const StyledSectionHeader = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
}))

export const StyledSectionTitle = styled(Typography)(({ theme }) => ({
    fontWeight: 600,
    color: theme.palette.text.primary,
}))

// Rendered as a real router link (component={RouterLink}) so right-click works
export const StyledViewAllButton = styled(Button)<{ component?: React.ElementType; to?: string }>(({ theme }) => ({
    color: theme.palette.primary.main,
    fontSize: 14,
    fontWeight: 500,
    '&:hover': {
        backgroundColor: 'transparent',
        textDecoration: 'underline',
    },
}))

export const StyledCardScroller = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(2.5),
    overflowX: 'auto',
    paddingBottom: theme.spacing(2),
    '&::-webkit-scrollbar': {
        height: 6,
    },
    '&::-webkit-scrollbar-thumb': {
        backgroundColor: theme.palette.divider,
        borderRadius: 3,
    },
}))

export const StyledBottomSection = styled(Box)(({ theme }) => ({
    marginTop: theme.spacing(6),
    marginBottom: theme.spacing(6),
}))

export const StyledLoadingWrapper = styled(Box)({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 300,
})
